/** MODELUL EDITORULUI DE PARCAJ: locurile unui subsol, mutate, lipite, rotite,
 *  adăugate, șterse — și ce e în neregulă cu fiecare.
 *
 *  Funcții PURE peste lista de locuri (`SolvedSpace`, cum le citește
 *  `canonical.toPlan`), în cadrul DESENULUI. Ce atinge ecranul, istoricul și
 *  serverul stă în `useEditorParcaj.tsx`; ce traduce înapoi în document, în
 *  `scriere.ts`.
 *
 *  REGULILE sunt ale generatorului, repetate aici ca să se vadă pe loc, în timp
 *  ce tragi (`architecture/benzi/completare.py _Podea.incape`,
 *  `benzi/deservire.py`): un loc stă în conturul nivelului, nu peste corpul
 *  unei alei, nu peste un obstacol fix (stâlp, perete, cameră, rampă, culoar
 *  PMR), nu peste alt loc; și e DESERVIT — dintr-una din laturi, spre afară,
 *  după cel mult `GOL_DESERVIT` de gol se găsește cel puțin `ADANCIME_PROBA` de
 *  carosabil pe `ACOPERIRE` din lățimea laturii, fără alt loc între. Verdictul
 *  final rămâne al validatorului («Verificări»), pe documentul scris; aici e
 *  avertismentul, în culoare, înainte să salvezi.
 *
 *  Pragurile de mai jos au numele și valorile de acolo, ca o cifră schimbată
 *  într-un loc să se poată găsi în celălalt.
 */
import type { Pt } from "../types";
import type { SolvedSpace, SolverLane } from "../client";
import { axele, distLaSegment, inContur, inelAlee, inelDreptunghi, peAlee, seSuprapun,
         taieSegmentul, unghiulAleii, cutia, LATIME_ALEE_IMPLICITA, type Cutie } from "./geometrie";
import { inInel } from "../tronson/doc";

export type Loc = SolvedSpace;

/** Gabaritul unui loc (`benzi/stalpi.py`): 2,50 lățime; 5,00 adâncime la
 *  90° și cu capul înainte, 5,75 de-a lungul aleii la cel paralel. */
export const LATIME_LOC = 2.5;
export const LUNGIME_LOC = 5.0;
export const LUNGIME_LOC_PARALEL = 5.75;
/** Sub cât GOL muchia locului tras se lipește de a vecinului. */
export const LIPIRE = 0.30;
/** Până la câtă SUPRAPUNERE cu vecinul locul se împinge înapoi, lipit de el:
 *  jumătate de loc — tras mai adânc de atât, omul vrea dincolo de vecin. */
export const LIPIRE_PESTE = 1.25;
/** Cât de departe de linia rândului poate cădea un loc (pus cu un clic, sau
 *  tras liber) ca să fie «așezat» pe rând. Măsurat pe un clic cu mâna liberă:
 *  0,7 m în afara liniei. */
export const LIPIRE_RAND = 1.0;
/** Două locuri sunt «pe același rând» dacă unghiurile diferă cu cel mult atât. */
export const UNGHI_RAND = 2;
/** Un loc lăsat lângă altul cu unghiul diferit cu mai puțin de atât IA unghiul
 *  vecinului, înainte să se lipească pe poziție (Oana, 22.10.2026: «snap și la
 *  rotire»). Peste prag, omul l-a întors dinadins. */
export const UNGHI_LIPIRE = 5;
/** O alee nu se scurtează sub atât. */
export const ALEE_MIN = 1.0;
/** `benzi/deservire.py`: cât gol poate sta între loc și aleea lui, cât carosabil
 *  trebuie găsit dincolo de gol, și pe cât din latură. */
export const GOL_DESERVIT = 2.5;
export const ADANCIME_PROBA = 1.0;
export const ACOPERIRE = 0.9;
/** Cu cât se strânge locul întrebat de suprapunere: două locuri lipite pe
 *  muchie nu se suprapun (`_Podea.incape` tolerează 0,005 m² între locuri). */
export const STRANS = 0.01;
/** Pașii săgeților: fin, și cu ⇧. */
export const PAS_SAGEATA = 0.05;
export const PAS_SAGEATA_MARE = 0.5;
/** Un loc nou ia orientarea celui mai apropiat loc de până la atât; mai departe,
 *  pe a celei mai apropiate alei. */
export const VECIN_PENTRU_ORIENTARE = 6.0;

export type Fel = "loc" | "stalp" | "alee" | "camera" | "perete" | "rampa" | "culoar" | "contur";

/** Un obstacol fix al nivelului, ca inel, cu ce e — ca avertismentul să spună
 *  PESTE CE stă locul. */
export type Obstacol = { fel: Exclude<Fel, "loc" | "contur">; inel: Pt[]; cutie?: Cutie };

export type Obstacole = {
  /** Conturul planșeului; gol = nu se verifică. */
  contur: readonly Pt[];
  alei: readonly SolverLane[];
  fixe: readonly Obstacol[];
};

export type Conflict = {
  /** Peste ce stă locul — gol când nu stă peste nimic. */
  suprapune: Fel[];
  /** N-are alee din care să intre. */
  nedeservit: boolean;
};

export const inelLoc = (l: Loc, strans = 0): Pt[] =>
  inelDreptunghi({ cx: l.cx, cy: l.cy, w: l.w, d: l.d, deg: l.deg }, strans);

/** Obstacolele nivelului, cu cutiile lor calculate o dată. */
export function pregatesteObstacole(o: Obstacole): Obstacole {
  return { ...o, fixe: o.fixe.map((f) => ({ ...f, cutie: f.cutie ?? cutia(f.inel) })) };
}

/** Diferența de unghi, modulo 180 (un loc întors cu 180° e pe același rând). */
export function diferentaUnghi(a: number, b: number): number {
  const d = Math.abs(((a - b) % 180) + 180) % 180;
  return Math.min(d, 180 - d);
}

// ------------------------------------------------------------- conflicte ---

export function conflicte(l: Loc, altele: readonly Loc[], o: Obstacole): Conflict {
  const inel = inelLoc(l, STRANS);
  const suprapune = new Set<Fel>();
  if (o.contur.length >= 3 && !inContur(inel, o.contur)) suprapune.add("contur");
  for (const a of altele) {
    if (a.id !== l.id && seSuprapun(inel, inelLoc(a))) { suprapune.add("loc"); break; }
  }
  for (const al of o.alei) {
    if (seSuprapun(inel, inelAlee(al))) { suprapune.add("alee"); break; }
  }
  for (const f of o.fixe) {
    if (suprapune.has(f.fel)) continue;
    if (seSuprapun(inel, f.inel)) suprapune.add(f.fel);
  }
  return { suprapune: [...suprapune], nedeservit: !deservit(l, altele, o.alei) };
}

/** Câte sonde pe o latură. 8 × 0,3 m pe un loc de 2,50 — destul ca o alee care
 *  se termină la jumătatea locului să pice sub `ACOPERIRE`. */
const SONDE = 8;
const PAS_SONDA = 0.1;

/** Regula din `deservire.py`, pe puncte în loc de arii: pe fiecare latură,
 *  `SONDE` raze spre afară; o rază e deservită dacă dă de alee înainte de
 *  `GOL_DESERVIT`, aleea ține apoi `ADANCIME_PROBA`, și pe gol nu stă alt loc.
 *  Latura e deservită la `ACOPERIRE` din raze. */
export function deservit(l: Loc, altele: readonly Loc[], alei: readonly SolverLane[]): boolean {
  if (!alei.length) return false;
  const inel = inelLoc(l);
  const centru: Pt = [l.cx, l.cy];
  const vecini = altele.filter((a) => a.id !== l.id
    && Math.hypot(a.cx - l.cx, a.cy - l.cy) < (l.w + l.d + a.w + a.d) / 2 + GOL_DESERVIT)
    .map((a) => inelLoc(a));
  const peVreoAlee = (p: Pt) => alei.some((al) => peAlee(p, al));
  const peVreunLoc = (p: Pt) => vecini.some((v) => inInel(p, v));
  for (let i = 0; i < 4; i++) {
    const a = inel[i], b = inel[(i + 1) % 4];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 1e-6) continue;
    let nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L;
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
    // normala care IESE din loc
    if ((mx + nx - centru[0]) ** 2 + (my + ny - centru[1]) ** 2
        < (mx - centru[0]) ** 2 + (my - centru[1]) ** 2) { nx = -nx; ny = -ny; }
    let bune = 0;
    for (let k = 0; k < SONDE; k++) {
      const f = (k + 0.5) / SONDE;
      const sx = a[0] + (b[0] - a[0]) * f, sy = a[1] + (b[1] - a[1]) * f;
      let gol = -1;
      for (let t = PAS_SONDA; t <= GOL_DESERVIT + 1e-9; t += PAS_SONDA) {
        const p: Pt = [sx + nx * t, sy + ny * t];
        if (peVreunLoc(p)) break;
        if (peVreoAlee(p)) { gol = t; break; }
      }
      if (gol < 0) continue;
      let tine = true;
      for (let t = gol + PAS_SONDA; t <= gol + ADANCIME_PROBA + 1e-9; t += PAS_SONDA) {
        if (!peVreoAlee([sx + nx * t, sy + ny * t])) { tine = false; break; }
      }
      if (tine) bune += 1;
    }
    if (bune >= ACOPERIRE * SONDE) return true;
  }
  return false;
}

// ---------------------------------------------------------------- mutarea ---

/** Locul mutat cu `(dx, dy)` în cadrul desenului. Implicit ALUNECĂ PE O AXĂ A
 *  LUI (Oana, 22.10.2026): pe `u` (rândul) sau pe `v` (adâncimea — «și sus-jos»,
 *  pentru un loc paralel cu aleea), după `axa`; se păstrează doar componenta
 *  de-a lungul ei. Cu `liber`, mișcarea e cea a mâinii. */
export function muta(l: Loc, dx: number, dy: number, liber = false, axa: Axa = "u"): Loc {
  if (liber) return { ...l, cx: l.cx + dx, cy: l.cy + dy };
  const a = axele(l.deg)[axa];
  const t = dx * a[0] + dy * a[1];
  return { ...l, cx: l.cx + a[0] * t, cy: l.cy + a[1] * t };
}

export type Axa = "u" | "v";
/** De la câtă mișcare se hotărăște pe ce axă alunecă locul tras. */
export const PRAG_AXA = 0.25;
/** Pe care axă a locului e mai mult drumul `(dx, dy)` al mâinii. */
export function axaTragerii(l: Loc, dx: number, dy: number): Axa {
  const { u, v } = axele(l.deg);
  return Math.abs(dx * u[0] + dy * u[1]) >= Math.abs(dx * v[0] + dy * v[1]) ? "u" : "v";
}

/** Pasul cu săgețile, pe axele locului: `du` de-a lungul rândului, `dv`
 *  perpendicular. */
export function pas(l: Loc, du: number, dv: number): Loc {
  const { u, v } = axele(l.deg);
  return { ...l, cx: l.cx + u[0] * du + v[0] * dv, cy: l.cy + u[1] * du + v[1] * dv };
}

export function roteste(l: Loc): Loc {
  return rotesteLa(l, l.deg + 90);
}

/** Locul întors la unghiul `deg` (în grade, orice valoare), în jurul centrului
 *  — pentru mânerul de rotire și câmpul din bară: un loc așezat strâmb față de
 *  alee se îndreaptă la unghiul aleii, nu la un multiplu de 90°. */
export function rotesteLa(l: Loc, deg: number): Loc {
  return { ...l, deg: ((deg % 360) + 360) % 360 };
}

/** Unghiul la care mânerul tras în `p` întoarce locul cu centrul în `(cx, cy)`:
 *  mânerul stă pe axa `v` (adâncimea, `deg + 90`), deci unghiul locului e cu
 *  90° în urma direcției spre mâner. `pas` rotunjește (1° liber, 15° cu ⇧). */
export function unghiulDinManer(cx: number, cy: number, p: Pt, pas = 1): number {
  const deg = (Math.atan2(p[1] - cy, p[0] - cx) * 180) / Math.PI - 90;
  return Math.round(deg / pas) * pas;
}

/** Unde stă mânerul de rotire al locului: pe axa `v`, la `dist` dincolo de
 *  fața locului. */
export function pozitiaManerului(l: Loc, dist: number): Pt {
  const { v } = axele(l.deg);
  const r = l.d / 2 + dist;
  return [l.cx + v[0] * r, l.cy + v[1] * r];
}

/** LIPIREA de vecini: un loc tras la mai puțin de `LIPIRE` de muchia unui
 *  vecin de pe același rând (același unghi) se așază lipit de el și aliniat pe
 *  rând; unul adus în fața sau în spatele altuia (rând dublu) se lipește pe
 *  adâncime. Se aplică cea mai mică corecție dintre candidate, ca locul să nu
 *  sară la un vecin mai departe. */
export function lipeste(l0: Loc, altele: readonly Loc[]): Loc {
  const l = lipesteUnghiul(l0, altele);
  const { u, v } = axele(l.deg);
  let best: { du: number; dv: number; cost: number } | null = null;
  // Lipirea de o muchie bate simpla aliniere pe rând, oricât de mică ar fi
  // corecția alinierii: `doarAliniere` o pune în urma oricărei lipiri.
  const propune = (du: number, dv: number, doarAliniere = false) => {
    const cost = Math.hypot(du, dv) + (doarAliniere ? 1e3 : 0);
    if (!best || cost < best.cost) best = { du, dv, cost };
  };
  for (const a of altele) {
    // doar vecinii cu aceeași orientare (întorși cu 180° e tot același rând);
    // unul la 90° nu are muchie comună cu rândul
    if (a.id === l.id || diferentaUnghi(a.deg, l.deg) > UNGHI_RAND) continue;
    const aw = a.w, ad = a.d;
    const ox = a.cx - l.cx, oy = a.cy - l.cy;
    const du = ox * u[0] + oy * u[1], dv = ox * v[0] + oy * v[1];
    // «aproape de muchie»: cu un gol sub `LIPIRE` sau peste vecin cu mai puțin
    // de `LIPIRE_PESTE`
    const laMuchie = (dist: number, tinta: number) =>
      dist > tinta - LIPIRE_PESTE && dist < tinta + LIPIRE;
    // pe același rând, alături: muchiile pe `u` se lipesc, rândul se aliniază
    const tintaU = (l.w + aw) / 2;
    if (Math.abs(dv) < LIPIRE_RAND && laMuchie(Math.abs(du), tintaU)) {
      propune(Math.sign(du) * (Math.abs(du) - tintaU), dv);
      continue;
    }
    // în față sau în spate (rând dublu): muchiile pe `v` se lipesc
    const tintaV = (l.d + ad) / 2;
    if (Math.abs(du) < (l.w + aw) / 2 - STRANS && laMuchie(Math.abs(dv), tintaV)) {
      propune(Math.abs(du) < LIPIRE ? du : 0, Math.sign(dv) * (Math.abs(dv) - tintaV));
      continue;
    }
    // pe același rând, dar nu lipit: doar alinierea pe rând
    if (Math.abs(dv) < LIPIRE_RAND && Math.abs(dv) > 1e-9 && Math.abs(du) < l.w + aw * 2) {
      propune(0, dv, true);
    }
  }
  if (!best) return l;
  const b: { du: number; dv: number } = best;
  return { ...l, cx: l.cx + u[0] * b.du + v[0] * b.dv, cy: l.cy + u[1] * b.du + v[1] * b.dv };
}

/** UNGHIUL vecinului, dacă al meu diferă de al lui cu mai puțin de
 *  `UNGHI_LIPIRE`: vecinul cel mai apropiat dintre cei la care aș ajunge cu o
 *  lipire pe poziție (o lățime și o adâncime distanță). Întors cu 180° e tot
 *  unghiul lui, deci se ia varianta (a, a + 180) mai apropiată de al meu. */
export function lipesteUnghiul(l: Loc, altele: readonly Loc[]): Loc {
  let vecin: Loc | null = null, dist = Infinity;
  for (const a of altele) {
    if (a.id === l.id) continue;
    const d = Math.hypot(a.cx - l.cx, a.cy - l.cy);
    const raza = (l.w + l.d + a.w + a.d) / 2 + LIPIRE_RAND;
    if (d > raza || d >= dist) continue;
    const dif = diferentaUnghi(a.deg, l.deg);
    if (dif < 1e-9 || dif > UNGHI_LIPIRE) continue;
    vecin = a; dist = d;
  }
  if (!vecin) return l;
  const a = vecin.deg, b = vecin.deg + 180;
  const dA = Math.abs(((a - l.deg) % 360 + 540) % 360 - 180);
  const dB = Math.abs(((b - l.deg) % 360 + 540) % 360 - 180);
  return rotesteLa(l, dA <= dB ? a : b);
}

// ------------------------------------------------------------------- alei ---

/** O alee a nivelului, cum o desenează planșa: axa și lățimea, cu id-ul
 *  obiectului `Lane` ca să se poată scrie înapoi. */
export type Alee = SolverLane & { eid: string };

/** Capătul `capat` al aleii dus în `p`, PE DIRECȚIA ei: aleea se lungește sau
 *  se scurtează, nu se rotește (Oana, 22.10.2026: «să pot să lungesc/scurtez
 *  momentan»). Proiecția lui `p` pe dreapta axei, oprită la `ALEE_MIN` de
 *  celălalt capăt ca aleea să nu se întoarcă pe dos. */
export function mutaCapatul(a: Alee, capat: 0 | 1, p: Pt): Alee {
  const fix: Pt = capat === 0 ? [a.x2, a.y2] : [a.x1, a.y1];
  const mobil: Pt = capat === 0 ? [a.x1, a.y1] : [a.x2, a.y2];
  const dx = mobil[0] - fix[0], dy = mobil[1] - fix[1];
  const L = Math.hypot(dx, dy);
  if (L < 1e-9) return a;
  const ux = dx / L, uy = dy / L;
  const t = Math.max(ALEE_MIN, (p[0] - fix[0]) * ux + (p[1] - fix[1]) * uy);
  const nou: Pt = [fix[0] + ux * t, fix[1] + uy * t];
  return capat === 0 ? { ...a, x1: nou[0], y1: nou[1] } : { ...a, x2: nou[0], y2: nou[1] };
}

export const lungimeaAleii = (a: SolverLane) => Math.hypot(a.x2 - a.x1, a.y2 - a.y1);

/** Mijlocul aleii. */
export const mijloculAleii = (a: SolverLane): Pt => [(a.x1 + a.x2) / 2, (a.y1 + a.y2) / 2];

/** Aleea întoarsă la unghiul `deg` (al axei, în grade), în jurul MIJLOCULUI ei,
 *  cu aceeași lungime (Oana, 22.10.2026: «aș vrea și rotire de alee»). */
export function rotesteAleea(a: Alee, deg: number): Alee {
  const [mx, my] = mijloculAleii(a);
  const L = lungimeaAleii(a) / 2;
  const r = (deg * Math.PI) / 180;
  const dx = Math.cos(r) * L, dy = Math.sin(r) * L;
  return { ...a, x1: mx - dx, y1: my - dy, x2: mx + dx, y2: my + dy };
}

/** Unde stă mânerul de rotire al aleii: perpendicular pe axă, din mijloc, la
 *  `dist` dincolo de marginea ei. Unghiul din mâner e `unghiulDinManer` pe
 *  mijloc: mânerul stă la `deg + 90`, exact ca la loc. */
export function manerulAleii(a: SolverLane, dist: number): Pt {
  const [mx, my] = mijloculAleii(a);
  const deg = (Math.atan2(a.y2 - a.y1, a.x2 - a.x1) * 180) / Math.PI;
  const { v } = axele(deg);
  const r = (a.w ?? LATIME_ALEE_IMPLICITA) / 2 + dist;
  return [mx + v[0] * r, my + v[1] * r];
}

// --------------------------------------------------------- cote de vecinătate ---

/** Până unde se caută vecinul unei laturi (Oana, 23.10.2026: «până la 10 m»). */
export const COTA_MAX = 10;
/** Cât se strânge latura la capete: un vecin LIPIT pe rând atinge locul exact
 *  în colț și ar da o cotă de 0 spre față sau spate, care nu spune nimic. */
const COTA_CAPAT = 0.02;
/** Cât poate intra un vecin peste latură și să fie tot «lipit» (0): generatorul
 *  lasă locurile să se calce cu milimetri între ele și cu banda aleii
 *  (`_Podea.incape`). Tăiată fâșia exact la 0, latura apropiată a vecinului
 *  cădea afară și cota sărea la latura lui de dincolo — 2,50 spre locul de
 *  alături, 6,00 spre alee (VM, 295b, 23.10.2026). */
const COTA_SUPRAPUNERE = 0.05;

export type Latura = "fata" | "spate" | "stanga" | "dreapta";

/** O cotă de vecinătate: din latura `latura` a locului, spre exterior, până la
 *  cel mai apropiat obiect (`fel`), la distanța `m`; `a` pe latură, `b` pe obiect. */
export type CotaVecin = { latura: Latura; fel: Fel; m: number; a: Pt; b: Pt };

/** Un inel de verificat, cu ce e și cutia lui (ca să se sară cele departe). */
type Tinta = { fel: Fel; inel: readonly Pt[]; cutie: Cutie; inchis: boolean };

/** COTELE DE VECINĂTATE ale locului `l` (Oana, 23.10.2026: «când mișc locuri să
 *  văd și niște cote față de alte obiecte în jur»): din fiecare latură, spre
 *  exterior, pe direcția locului, distanța până la primul obiect din fâșia
 *  laturii — alt loc, stâlp, perete, cameră, rampă, culoar PMR, marginea benzii
 *  unei alei, marginea planșeului —, numai sub `COTA_MAX`.
 *
 *  CUM: în cadrul laturii (`s` de-a lungul ei, `t` spre afară) fiecare latură de
 *  obiect se taie la dreptunghiul `[0, L] × [0, COTA_MAX]`, iar distanța e cel mai
 *  mic `t` rămas. Primul punct al unui poligon în fâșie stă pe conturul lui, deci
 *  laturile ajung; marginea planșeului e la fel un contur. `fata`/`spate` sunt
 *  laturile lățimii (spre alee / spre perete la un loc de 90°), `stanga`/`dreapta`
 *  ale adâncimii (spre vecinii de pe rând). Un obiect care stă PESTE loc dă 0 —
 *  e deja roșu în conflicte. */
export function coteVecinatate(l: Loc, altele: readonly Loc[], o: Obstacole): CotaVecin[] {
  const tinte: Tinta[] = [];
  const lim = Math.hypot(l.w, l.d) / 2 + COTA_MAX;
  const aproape = (c: Cutie) =>
    c.x1 >= l.cx - lim && c.x0 <= l.cx + lim && c.y1 >= l.cy - lim && c.y0 <= l.cy + lim;
  const pune = (fel: Fel, inel: readonly Pt[], c?: Cutie, inchis = true) => {
    if (inel.length < 2) return;
    const cutie = c ?? cutia(inel);
    if (aproape(cutie)) tinte.push({ fel, inel, cutie, inchis });
  };
  for (const a of altele) if (a.id !== l.id) pune("loc", inelLoc(a));
  for (const f of o.fixe) pune(f.fel, f.inel, f.cutie);
  for (const al of o.alei) pune("alee", inelAlee(al));
  if (o.contur.length >= 3) pune("contur", o.contur);

  const inel = inelLoc(l);       // colțurile: (−w/2,−d/2) (w/2,−d/2) (w/2,d/2) (−w/2,d/2)
  const laturi: [Latura, Pt, Pt][] = [
    ["spate", inel[0], inel[1]], ["dreapta", inel[1], inel[2]],
    ["fata", inel[2], inel[3]], ["stanga", inel[3], inel[0]],
  ];
  const out: CotaVecin[] = [];
  for (const [latura, p0, p1] of laturi) {
    const L = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
    if (L < 1e-6) continue;
    const ux = (p1[0] - p0[0]) / L, uy = (p1[1] - p0[1]) / L;
    // normala care IESE din loc
    let nx = -uy, ny = ux;
    const mx = (p0[0] + p1[0]) / 2, my = (p0[1] + p1[1]) / 2;
    if ((mx + nx - l.cx) ** 2 + (my + ny - l.cy) ** 2 < (mx - l.cx) ** 2 + (my - l.cy) ** 2) {
      nx = -nx; ny = -ny;
    }
    const laCadru = (p: Pt): Pt => {
      const x = p[0] - p0[0], y = p[1] - p0[1];
      return [x * ux + y * uy, x * nx + y * ny];
    };
    let best: { t: number; s: number; fel: Fel } | null = null;
    // chiar în fața mijlocului laturii: un obiect care o acoperă e lipit (0),
    // oricât de adânc ar intra — conturul lui ar da altfel latura de dincolo
    const fata: Pt = [mx + nx * 0.01, my + ny * 0.01];
    for (const tg of tinte) {
      if (tg.inchis && tg.fel !== "contur" && inInel(fata, tg.inel as Pt[])) {
        best = { t: 0, s: L / 2, fel: tg.fel };
        break;
      }
      const r = tg.inel.map(laCadru);
      const n = tg.inchis ? r.length : r.length - 1;
      for (let i = 0; i < n; i++) {
        const seg = taieSegmentul(r[i], r[(i + 1) % r.length], COTA_CAPAT, L - COTA_CAPAT,
                                  -COTA_SUPRAPUNERE, COTA_MAX);
        if (!seg) continue;
        for (const q of seg) {
          const t = Math.max(0, q[1]);
          if (!best || t < best.t) best = { t, s: q[0], fel: tg.fel };
        }
      }
    }
    if (!best) continue;
    const a: Pt = [p0[0] + ux * best.s, p0[1] + uy * best.s];
    out.push({ latura, fel: best.fel, m: best.t, a,
               b: [a[0] + nx * best.t, a[1] + ny * best.t] });
  }
  return out;
}

// ----------------------------------------------------------------- loc nou ---

export function urmatorulNumar(locuri: readonly Loc[]): number {
  return locuri.reduce((m, l) => Math.max(m, l.n ?? 0), 0) + 1;
}

/** Un loc nou cu centrul în `p`: orientarea celui mai apropiat loc (sub
 *  `VECIN_PENTRU_ORIENTARE`), altfel a celei mai apropiate alei — rândul e
 *  paralel cu aleea, adâncimea spre ea —, apoi lipit de vecini. */
export function locNou(p: Pt, locuri: readonly Loc[], alei: readonly SolverLane[],
                       id: string): Loc {
  let deg = 0, form: Loc["form"] = "perp", d = LUNGIME_LOC;
  let vecin: Loc | null = null, dv = VECIN_PENTRU_ORIENTARE;
  for (const l of locuri) {
    const dist = Math.hypot(l.cx - p[0], l.cy - p[1]);
    if (dist < dv) { dv = dist; vecin = l; }
  }
  if (vecin) {
    deg = vecin.deg; form = vecin.form ?? "perp"; d = vecin.d;
  } else if (alei.length) {
    let cea: SolverLane | null = null, da = Infinity;
    for (const a of alei) {
      const dist = distLaSegment(p, [a.x1, a.y1], [a.x2, a.y2]);
      if (dist < da) { da = dist; cea = a; }
    }
    if (cea) deg = unghiulAleii(cea);
  }
  const l: Loc = { id, cx: p[0], cy: p[1], w: LATIME_LOC, d, deg, kind: "std",
                   n: urmatorulNumar(locuri), form };
  return lipeste(l, locuri);
}

// ----------------------------------------------------------------- istoric ---

/** Teancul de anulări, pe LISTE de locuri — ca `tronson/editor/model.Istoric`,
 *  care ține documente întregi din același motiv: o copie e ieftină și nu
 *  poate fi greșită. */
export class Istoric<T> {
  private inapoi: T[] = [];
  private inainte: T[] = [];
  constructor(private readonly adancime = 50) {}

  pune(x: T) {
    this.inapoi.push(x);
    if (this.inapoi.length > this.adancime) this.inapoi.shift();
    this.inainte = [];
  }
  anuleaza(curent: T): T | null {
    const x = this.inapoi.pop();
    if (x === undefined) return null;
    this.inainte.push(curent);
    return x;
  }
  refa(curent: T): T | null {
    const x = this.inainte.pop();
    if (x === undefined) return null;
    this.inapoi.push(curent);
    return x;
  }
  goleste() { this.inapoi = []; this.inainte = []; }
  get poateAnula() { return this.inapoi.length > 0; }
  get poateReface() { return this.inainte.length > 0; }
}
